/**
 * Create a software product (XER admin only).
 *
 * This is the whole point of the products table: a new downloadable product
 * used to need a migration and a deploy. Two things about this dialog are
 * load-bearing rather than cosmetic:
 *
 * 1. **No audience is preselected.** Audience decides who can ever see the
 *    product's installers and is IMMUTABLE once created (a DB trigger pins
 *    it) — changing visibility means creating a different product key, which
 *    is exactly what `uc_flasher` → `uc_flasher_operator` had to do. A default
 *    would let the choice be made by not making it.
 * 2. **The key can never change either**, because it is baked into the S3
 *    prefix of every release published under it. It is validated while typing
 *    against the same rule the database enforces.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';
import { useCreateProductMutation } from './Downloads.queries';
import {
  AUDIENCE_CHOICES,
  AUDIENCE_CUSTOMER_WARNING,
  AUDIENCE_REQUIRED_HINT,
  PRODUCT_KEY_HINT,
  canCreateProduct,
  suggestProductKey,
  validateProductKey,
} from './productCatalog';
import type { SoftwareAudience } from '../../types/downloads';

interface NewProductDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new product's key once it exists, so the panel can select it. */
  onCreated?: (key: string) => void;
}

export const NewProductDialog: React.FC<NewProductDialogProps> = ({ open, onClose, onCreated }) => {
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);
  const [audience, setAudience] = useState<SoftwareAudience | ''>('');

  const createMutation = useCreateProductMutation();
  const busy = createMutation.isPending;
  // Only complain about a key the admin has actually started typing — an
  // untouched field is not yet a mistake.
  const keyError = key || keyEdited ? validateProductKey(key) : null;
  // The submit rule lives in productCatalog so it can be tested without a DOM
  // — in particular the "an audience must be CHOSEN" half, which is the one
  // that cannot be undone after the fact.
  const canSubmit = !busy && canCreateProduct({ label, key, audience });

  const reset = () => {
    setLabel('');
    setKey('');
    setKeyEdited(false);
    setAudience('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleLabelChange = (value: string) => {
    setLabel(value);
    // Suggest a key until the admin takes over the field; never overwrite a
    // key they typed themselves.
    if (!keyEdited) setKey(suggestProductKey(value));
  };

  const handleCreate = async () => {
    if (audience === '') return;
    try {
      const created = await createMutation.mutateAsync({
        key,
        label: label.trim(),
        audience,
      });
      toast.success(`Created ${created.label}`);
      onCreated?.(created.key);
      reset();
      onClose();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to create product'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="new-product-dialog"
    >
      <DialogTitle>New software product</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A product is a column on /downloads and a folder in S3. Its key and its audience can never
          be changed afterwards — publishing to a different audience means creating another product.
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Display name"
          placeholder="e.g. XER Ground Station"
          value={label}
          onChange={(e) => handleLabelChange(e.target.value)}
          disabled={busy}
          sx={{ mb: 2 }}
          inputProps={{ 'data-testid': 'new-product-label' }}
        />

        <TextField
          fullWidth
          size="small"
          label="Product key (permanent)"
          value={key}
          onChange={(e) => {
            setKeyEdited(true);
            setKey(e.target.value);
          }}
          disabled={busy}
          error={!!keyError}
          helperText={keyError ?? PRODUCT_KEY_HINT}
          sx={{ mb: 2 }}
          inputProps={{ 'data-testid': 'new-product-key' }}
        />

        <FormControl disabled={busy}>
          <FormLabel id="new-product-audience-label">Who may see this product?</FormLabel>
          <RadioGroup
            aria-labelledby="new-product-audience-label"
            value={audience}
            onChange={(e) => setAudience(e.target.value as SoftwareAudience)}
          >
            {/* Rendered from AUDIENCE_CHOICES rather than written inline: the
                copy is the only warning before an irreversible decision, so it
                lives where a test can hold it to naming the consequence. */}
            {AUDIENCE_CHOICES.map((choice) => (
              <FormControlLabel
                key={choice.value}
                value={choice.value}
                control={
                  <Radio
                    data-testid={`new-product-audience-${
                      choice.value === 'customer' ? 'customer' : 'internal'
                    }`}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{choice.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {choice.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        {audience === '' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {AUDIENCE_REQUIRED_HINT}
          </Alert>
        )}
        {audience === 'customer' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {AUDIENCE_CUSTOMER_WARNING}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={!canSubmit}
          data-testid="new-product-create"
        >
          Create product
        </Button>
      </DialogActions>
    </Dialog>
  );
};
